import { useRef, useEffect, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import {
  Clock, Zap, DollarSign, ChevronDown, Scale, Building2, Send,
  ArrowRight, Shield, Globe, TrendingUp, Users,
  Check, X, Landmark, Mail
} from 'lucide-react';

/* ──────────────────── helpers ──────────────────── */

function CountUp({ target, suffix = '', prefix = '', duration = 1.5 }: {
  target: number; suffix?: string; prefix?: string; duration?: number;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.05 });
  const [val, setVal] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    const step = (now: number) => {
      const t = Math.min((now - start) / (duration * 1000), 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setVal(Math.round(eased * target));
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [inView, target, duration]);

  return <span ref={ref}>{prefix}{val.toLocaleString('es-ES')}{suffix}</span>;
}

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: (i: number = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.55, delay: i * 0.1, ease: [.22, 1, .36, 1] as [number, number, number, number] },
  }),
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.85 },
  visible: (i: number = 0) => ({
    opacity: 1, scale: 1,
    transition: { duration: 0.6, delay: i * 0.15, ease: [.22, 1, .36, 1] as [number, number, number, number] },
  }),
};

function Section({ children, className = '', id }: {
  children: React.ReactNode; className?: string; id?: string;
}) {
  return (
    <section id={id} className={`relative py-24 sm:py-32 px-5 sm:px-8 ${className}`}>
      <div className="mx-auto max-w-6xl">{children}</div>
    </section>
  );
}

function SectionTitle({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.05 });
  return (
    <motion.h2
      ref={ref}
      initial="hidden"
      animate={inView ? 'visible' : 'hidden'}
      variants={fadeUp}
      className={`font-heading text-3xl sm:text-4xl font-bold text-white mb-12 sm:mb-16 ${className}`}
    >
      {children}
    </motion.h2>
  );
}

function Card({ children, className = '', borderColor }: {
  children: React.ReactNode; className?: string; borderColor?: string;
}) {
  return (
    <div
      className={`rounded-xl bg-[#141E33] p-6 sm:p-8 shadow-lg shadow-black/20 transition-transform duration-300 hover:-translate-y-1 ${className}`}
      style={borderColor ? { borderTop: `3px solid ${borderColor}` } : undefined}
    >
      {children}
    </div>
  );
}

/* ──────────────────── ARROW CONNECTOR ──────────────────── */

function AnimatedArrow() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, scaleX: 0 }}
      animate={inView ? { opacity: 1, scaleX: 1 } : {}}
      transition={{ duration: 0.5, ease: 'easeOut' }}
      className="hidden lg:flex items-center justify-center origin-left"
    >
      <ArrowRight className="w-8 h-8 text-[#00D4AA]" />
    </motion.div>
  );
}

/* ──────────────────── SECTIONS ──────────────────── */

function Hero() {
  return (
    <section className="relative min-h-screen flex items-center justify-center px-5 overflow-hidden">
      {/* Glow */}
      <div className="pointer-events-none absolute top-[-20%] right-[-10%] w-[600px] h-[600px] rounded-full bg-[#00D4AA]/8 blur-[160px]" />
      <div className="relative z-10 text-center max-w-4xl mx-auto">
        <motion.span
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="inline-block mb-6 px-4 py-1.5 rounded-full border border-white/10 text-xs tracking-widest text-[#8899AA] uppercase"
        >
          Ronda Seed &middot; Abril 2026 &middot; Confidencial
        </motion.span>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.35, duration: 0.7, ease: [.22, 1, .36, 1] as [number, number, number, number] }}
          className="mb-5 flex justify-center"
        >
          <img
            src="/assets/migro-logo-green.png"
            alt="MIGRO"
            className="h-20 sm:h-28 md:h-36 w-auto brightness-0 invert"
          />
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55, duration: 0.6 }}
          className="font-heading italic text-[#00D4AA] text-xl sm:text-2xl md:text-3xl mb-6"
        >
          El pasaporte del futuro.
        </motion.p>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.6 }}
          className="text-[#E8ECF1] text-base sm:text-lg md:text-xl max-w-2xl mx-auto leading-relaxed"
        >
          Infraestructura financiera y legal para los 281&nbsp;millones de personas que se mueven por el mundo.
        </motion.p>
      </div>

      {/* Scroll indicator */}
      <motion.div
        className="absolute bottom-10 left-1/2 -translate-x-1/2"
        animate={{ y: [0, 10, 0] }}
        transition={{ repeat: Infinity, duration: 2, ease: 'easeInOut' }}
      >
        <ChevronDown className="w-6 h-6 text-[#00D4AA]/60" />
      </motion.div>
    </section>
  );
}

/* ── 10 Verdades ── */

const FACTS = [
  { text: 'La migración es el producto más caro del mundo: la gente paga con su vida.', highlight: true },
  { text: '281M de personas viven fuera de su país. Ninguna plataforma les sirve de verdad.' },
  { text: 'El dinero ya no se cambia en papel ni de moneda, sino de plataformas: de Bizum a Yape, de Revolut a Nequi.' },
  { text: 'Un migrante paga hasta 15\u202F€ a Western Union por enviar 300\u202F€ a su familia.' },
  { text: '73% de migrantes en España no acceden a banca en su primer año. Son invisibles.' },
  { text: '12.000 millones de EUR salen de España a LATAM cada año en remesas. Es un río de dinero sin dueño.' },
  { text: 'Un trámite de extranjería tarda 6-18 meses. Con tecnología, podría ser 6 semanas.' },
  { text: 'El mercado legal de inmigración en España vale 2.000M\u202F€ y funciona con papel y fax.' },
  { text: 'Cada migrante que consigue sus papeles se convierte en cliente bancario. Es un flywheel perfecto.' },
  { text: 'En 2030, 1 de cada 4 trabajadores en España será inmigrante. El futuro ya está aquí.' },
];

function TenTruths() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.05 });

  return (
    <Section id="verdades">
      <SectionTitle>10 verdades que nadie te cuenta sobre migración y dinero</SectionTitle>
      <div ref={ref} className="grid gap-4 sm:gap-5 sm:grid-cols-2">
        {FACTS.map((f, i) => (
          <motion.div
            key={i}
            custom={i}
            initial="hidden"
            animate={inView ? 'visible' : 'hidden'}
            variants={fadeUp}
            className={`group flex items-start gap-4 rounded-xl p-5 transition-colors ${
              f.highlight
                ? 'bg-[#FF6B6B]/10 border border-[#FF6B6B]/30'
                : 'bg-[#141E33] border border-white/5 hover:border-[#00D4AA]/20'
            }`}
          >
            <span
              className={`flex-none flex items-center justify-center w-9 h-9 rounded-full text-sm font-bold ${
                f.highlight
                  ? 'bg-[#FF6B6B]/20 text-[#FF6B6B]'
                  : 'bg-[#00D4AA]/10 text-[#00D4AA]'
              }`}
            >
              {i + 1}
            </span>
            <p className={`text-sm sm:text-base leading-relaxed ${f.highlight ? 'text-[#FF6B6B] font-semibold' : 'text-[#E8ECF1]'}`}>
              {f.text}
            </p>
          </motion.div>
        ))}
      </div>
    </Section>
  );
}

/* ── Problema ── */

const PROBLEMS = [
  {
    icon: Clock, title: 'Burocracia letal', color: '#FF6B6B',
    desc: '6-18 meses para un permiso. Citas imposibles. Abogados que cobran sin garantías.',
  },
  {
    icon: Zap, title: 'Exclusión financiera', color: '#FFD166',
    desc: 'Sin cuenta bancaria, sin tarjeta, sin historial. Invisibles para el sistema.',
  },
  {
    icon: DollarSign, title: 'Remesas sangrantes', color: '#00D4AA',
    desc: 'Hasta un 8% en comisiones. Western Union cobra 15\u202F€ por enviar 300\u202F€.',
  },
];

function Problem() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.05 });

  return (
    <Section id="problema">
      <SectionTitle>6,5 millones de personas invisibles para el sistema financiero español.</SectionTitle>
      <div ref={ref} className="grid gap-6 sm:grid-cols-3">
        {PROBLEMS.map((p, i) => (
          <motion.div key={i} custom={i} initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={fadeUp}>
            <Card borderColor={p.color}>
              <p.icon className="w-8 h-8 mb-4" style={{ color: p.color }} />
              <h3 className="text-lg font-bold text-white mb-2">{p.title}</h3>
              <p className="text-sm text-[#8899AA] leading-relaxed">{p.desc}</p>
            </Card>
          </motion.div>
        ))}
      </div>
    </Section>
  );
}

/* ── Solución / Flywheel ── */

const PILLARS = [
  { icon: Scale, title: 'Legal', desc: 'Papeles resueltos con abogados verificados y seguimiento en tiempo real.' },
  { icon: Building2, title: 'Banking', desc: 'Cuenta, tarjeta e historial financiero desde el día uno.' },
  { icon: Send, title: 'Remesas', desc: 'Envía dinero a casa por 2.99\u202F€ en vez de 15\u202F€. Blockchain + USDC.' },
];

function Solution() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.05 });

  return (
    <Section id="solucion">
      <motion.div initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={fadeUp} ref={ref}>
        <h2 className="font-heading text-4xl sm:text-5xl font-bold text-[#00D4AA] mb-3">MIGRO</h2>
        <p className="font-heading italic text-white text-xl sm:text-2xl mb-4">El Uber de la migración.</p>
        <p className="text-[#E8ECF1] text-base sm:text-lg max-w-3xl mb-14 leading-relaxed">
          Una sola plataforma donde el migrante resuelve sus papeles, abre una cuenta bancaria,
          recibe su tarjeta y envía dinero a casa.
        </p>
      </motion.div>

      <div className="grid gap-6 lg:grid-cols-[1fr_auto_1fr_auto_1fr] items-center">
        {PILLARS.flatMap((p, i) => {
          const items = [
            <motion.div
              key={`card-${i}`}
              custom={i}
              initial="hidden"
              animate={inView ? 'visible' : 'hidden'}
              variants={scaleIn}
            >
              <Card className="text-center">
                <p.icon className="w-10 h-10 text-[#00D4AA] mx-auto mb-4" />
                <h3 className="text-lg font-bold text-[#00D4AA] mb-2">{p.title}</h3>
                <p className="text-sm text-[#8899AA]">{p.desc}</p>
              </Card>
            </motion.div>,
          ];
          if (i < 2) items.push(<AnimatedArrow key={`arrow-${i}`} />);
          return items;
        })}
      </div>

      <motion.p
        initial="hidden"
        animate={inView ? 'visible' : 'hidden'}
        variants={fadeUp}
        custom={4}
        className="mt-12 text-center font-heading italic text-[#FFD166] text-base sm:text-lg"
      >
        Cada usuario legal se convierte en usuario bancario.
      </motion.p>
    </Section>
  );
}

/* ── Mercado ── */

const MARKET = [
  { label: 'TAM', value: 14600, suffix: 'M€', desc: 'Remesas + Legal + Banking migrantes ES', size: 'w-48 h-48 sm:w-56 sm:h-56' },
  { label: 'SAM', value: 2400, suffix: 'M€', desc: 'LATAM remitters + clientes legales activos', size: 'w-40 h-40 sm:w-44 sm:h-44' },
  { label: 'SOM', value: 480, suffix: 'M€', desc: 'Meta Y3: 20% SAM con ambas verticales', size: 'w-32 h-32 sm:w-36 sm:h-36' },
];

function Market() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.05 });

  return (
    <Section id="mercado">
      <SectionTitle>{'Un mercado de 14.600M\u202F€ solo en España.'}</SectionTitle>
      <div ref={ref} className="flex flex-col sm:flex-row items-center justify-center gap-10 sm:gap-14">
        {MARKET.map((m, i) => (
          <motion.div
            key={i}
            custom={i}
            initial="hidden"
            animate={inView ? 'visible' : 'hidden'}
            variants={scaleIn}
            className="text-center"
          >
            <div className={`${m.size} mx-auto rounded-full border-2 border-[#00D4AA]/30 bg-[#00D4AA]/5 flex flex-col items-center justify-center mb-4`}>
              <span className="text-xs tracking-widest text-[#00D4AA] uppercase mb-1">{m.label}</span>
              <span className="text-2xl sm:text-3xl font-bold text-white">
                <CountUp target={m.value} suffix={m.suffix} />
              </span>
            </div>
            <p className="text-xs sm:text-sm text-[#8899AA] max-w-[200px] mx-auto">{m.desc}</p>
          </motion.div>
        ))}
      </div>
    </Section>
  );
}

/* ── Por qué ahora ── */

const WHY_NOW = [
  { icon: Shield, title: 'MiCA en vigor', desc: 'USDC es la única stablecoin compliant en la UE.' },
  { icon: Globe, title: 'Base L2 con $4B+ TVL', desc: 'Blockchain viable para pagos reales por primera vez.' },
  { icon: Users, title: 'Regularización masiva 2026', desc: 'Millones de nuevos residentes legales.' },
  { icon: TrendingUp, title: 'Product-market fit probado', desc: '18K+ EUR MRR, CPL de ~1\u202F€.' },
];

function WhyNow() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.05 });

  return (
    <Section id="por-que-ahora">
      <SectionTitle>Por qué ahora.</SectionTitle>
      <div ref={ref} className="grid gap-4">
        {WHY_NOW.map((w, i) => (
          <motion.div
            key={i} custom={i}
            initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={fadeUp}
            className="flex items-start gap-5 rounded-xl bg-[#141E33] border border-white/5 p-5 sm:p-6"
          >
            <w.icon className="w-7 h-7 flex-none text-[#00D4AA] mt-0.5" />
            <div>
              <h3 className="text-base font-bold text-[#00D4AA] mb-1">{w.title}</h3>
              <p className="text-sm text-[#8899AA]">{w.desc}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </Section>
  );
}

/* ── Tracción ── */

const STATS = [
  { value: 18, suffix: 'K+', label: 'EUR/mes — MRR Lawyer SaaS', color: '#00D4AA' },
  { value: 1, prefix: '~', suffix: '€', label: 'Coste por lead', color: '#FFD166' },
  { value: 50, suffix: '+', label: 'Abogados suscritos activos', color: '#FF6B6B' },
  { value: 6.5, suffix: 'M', label: 'TAM migrantes en España', color: '#00D4AA' },
];

const BAR_DATA = [
  { label: 'Ene', pct: 35 },
  { label: 'Feb', pct: 52 },
  { label: 'Mar', pct: 72 },
  { label: 'Abr', pct: 100 },
];

function Traction() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.05 });

  return (
    <Section id="traccion">
      <SectionTitle>Tracción real. No powerpoints.</SectionTitle>
      <div ref={ref} className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-12">
        {STATS.map((s, i) => (
          <motion.div key={i} custom={i} initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={scaleIn}>
            <Card className="text-center">
              <p className="text-3xl sm:text-4xl font-bold mb-2" style={{ color: s.color }}>
                <CountUp target={s.value} suffix={s.suffix} prefix={s.prefix || ''} />
              </p>
              <p className="text-xs sm:text-sm text-[#8899AA]">{s.label}</p>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Mini bar chart */}
      <motion.div
        initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={fadeUp} custom={4}
        className="flex items-end justify-center gap-6 sm:gap-10 h-40 mb-10"
      >
        {BAR_DATA.map((b, i) => (
          <div key={i} className="flex flex-col items-center gap-2">
            <motion.div
              initial={{ height: 0 }}
              animate={inView ? { height: `${b.pct}%` } : {}}
              transition={{ duration: 0.8, delay: 0.3 + i * 0.12, ease: [.22, 1, .36, 1] as [number, number, number, number] }}
              className="w-10 sm:w-14 rounded-t-md bg-gradient-to-t from-[#00D4AA]/60 to-[#00D4AA]"
            />
            <span className="text-xs text-[#8899AA]">{b.label}</span>
          </div>
        ))}
      </motion.div>

      <motion.p
        initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={fadeUp} custom={5}
        className="text-center font-heading italic text-[#E8ECF1]/80 text-sm sm:text-base"
      >
        Vertical legal activa y generando revenue. El banking es el paso natural.
      </motion.p>
    </Section>
  );
}

/* ── Producto / How it works ── */

const STEPS = [
  { num: '01', title: 'Busca trámite', desc: 'El migrante selecciona su trámite legal y sube documentos.' },
  { num: '02', title: 'Match con abogado', desc: 'IA asigna al abogado óptimo. Seguimiento en tiempo real.' },
  { num: '03', title: 'Abre cuenta', desc: 'Verificación KYC integrada. IBAN español en minutos.' },
  { num: '04', title: 'Envía dinero', desc: 'Remesas a LATAM por 2.99\u202F€ via USDC en Base L2.' },
];

const TECH = ['React Native', 'Next.js', 'Node.js', 'Base L2', 'Circle USDC', 'Solidity', 'Unnax BaaS'];

function Product() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.05 });

  return (
    <Section id="producto">
      <SectionTitle>Cómo funciona</SectionTitle>
      <div ref={ref} className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4 mb-14">
        {STEPS.map((s, i) => (
          <motion.div key={i} custom={i} initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={fadeUp}>
            <Card className="relative">
              <span className="text-5xl font-bold text-[#00D4AA]/15 absolute top-3 right-4 leading-none">{s.num}</span>
              <h3 className="text-base font-bold text-[#00D4AA] mb-2">{s.title}</h3>
              <p className="text-sm text-[#8899AA]">{s.desc}</p>
            </Card>
          </motion.div>
        ))}
      </div>
      <div className="flex flex-wrap justify-center gap-3">
        {TECH.map(t => (
          <span key={t} className="px-3 py-1.5 rounded-full text-xs bg-[#162038] text-[#8899AA] border border-white/5">
            {t}
          </span>
        ))}
      </div>
    </Section>
  );
}

/* ── Modelo de Negocio ── */

const REVENUE_ROWS = [
  { stream: 'Remesas', margin: '1.50-2.99\u202F€', vol: '3-4 envíos/user/mes', revenue: '5.1-6.8K\u202F€' },
  { stream: 'Interchange EU', margin: '0.81\u202F€/tarjeta', vol: '1K tarjetas', revenue: '810\u202F€' },
  { stream: 'Interchange LATAM', margin: '3-5\u202F€/tarjeta', vol: 'Fase 3+', revenue: 'Escalable' },
  { stream: 'Lawyer SaaS', margin: '149-499\u202F€/sem', vol: '50-100 abogados', revenue: '30-200K\u202F€' },
];

function BusinessModel() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.05 });

  return (
    <Section id="modelo">
      <SectionTitle>Modelo de negocio — cuatro fuentes de revenue, todas recurrentes.</SectionTitle>

      {/* Desktop table */}
      <motion.div ref={ref} initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={fadeUp}
        className="hidden sm:block overflow-x-auto rounded-xl border border-white/5"
      >
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-white/10 text-[#8899AA]">
              <th className="px-6 py-4 font-semibold">Revenue Stream</th>
              <th className="px-6 py-4 font-semibold">Margen unitario</th>
              <th className="px-6 py-4 font-semibold">Volumen 1K users</th>
              <th className="px-6 py-4 font-semibold">Revenue/mes</th>
            </tr>
          </thead>
          <tbody>
            {REVENUE_ROWS.map((r, i) => (
              <tr key={i} className={i % 2 ? 'bg-[#141E33]' : 'bg-[#0F1A2E]'}>
                <td className="px-6 py-4 font-bold text-white">{r.stream}</td>
                <td className="px-6 py-4 text-[#E8ECF1]">{r.margin}</td>
                <td className="px-6 py-4 text-[#E8ECF1]">{r.vol}</td>
                <td className="px-6 py-4 text-[#00D4AA] font-semibold">{r.revenue}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </motion.div>

      {/* Mobile cards */}
      <div className="sm:hidden grid gap-4">
        {REVENUE_ROWS.map((r, i) => (
          <motion.div key={i} custom={i} initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={fadeUp}>
            <Card>
              <h3 className="text-base font-bold text-white mb-3">{r.stream}</h3>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><span className="text-[#8899AA]">Margen: </span><span className="text-[#E8ECF1]">{r.margin}</span></div>
                <div><span className="text-[#8899AA]">Vol: </span><span className="text-[#E8ECF1]">{r.vol}</span></div>
                <div className="col-span-2"><span className="text-[#8899AA]">Revenue: </span><span className="text-[#00D4AA] font-semibold">{r.revenue}</span></div>
              </div>
            </Card>
          </motion.div>
        ))}
      </div>

      <motion.p
        initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={fadeUp} custom={5}
        className="mt-10 text-center font-heading italic text-[#FFD166] text-sm sm:text-base"
      >
        Las remesas son el core revenue del banking: 4-5x más que interchange en EU.
      </motion.p>
    </Section>
  );
}

/* ── Unit Economics ── */

const COST_ITEMS = [
  { label: 'Gas (Base L2)', cost: '0.01\u202F€' },
  { label: 'CCTP (Circle)', cost: '0.05\u202F€' },
  { label: 'Off-ramp (Bridge/Bitso)', cost: '0.50-1.00\u202F€' },
  { label: 'FX spread', cost: '0.30\u202F€' },
  { label: 'Compliance/KYT', cost: '0.05\u202F€' },
];

const UNIT_METRICS = [
  { label: 'LTV/CAC', value: '>10x', color: '#00D4AA' },
  { label: 'Payback', value: '<2 meses', color: '#FFD166' },
  { label: 'CAC banking', value: '~0\u202F€', color: '#00D4AA' },
  { label: 'LTV anual', value: '200-400\u202F€', color: '#FF6B6B' },
];

function UnitEconomics() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.05 });

  return (
    <Section id="unit-economics">
      <SectionTitle>Unit economics</SectionTitle>
      <div ref={ref} className="grid gap-8 lg:grid-cols-2">
        {/* Left: Cost breakdown */}
        <motion.div initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={fadeUp}>
          <Card>
            <h3 className="text-base font-bold text-white mb-5">Coste por remesa</h3>
            <div className="space-y-3 mb-6">
              {COST_ITEMS.map(c => (
                <div key={c.label} className="flex items-center justify-between text-sm">
                  <span className="text-[#8899AA]">{c.label}</span>
                  <span className="text-[#E8ECF1] font-mono">{c.cost}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-white/10 pt-4 flex justify-between text-sm font-bold">
              <span className="text-white">Total coste</span>
              <span className="text-[#FFD166] font-mono">{'~1.00-1.40\u202F€'}</span>
            </div>
            <div className="mt-2 flex justify-between text-sm font-bold">
              <span className="text-white">Cobro al usuario</span>
              <span className="text-[#00D4AA] font-mono">{'2.99-3.99\u202F€'}</span>
            </div>
          </Card>
        </motion.div>

        {/* Right: Metric cards */}
        <div className="grid grid-cols-2 gap-4">
          {UNIT_METRICS.map((m, i) => (
            <motion.div key={i} custom={i + 1} initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={scaleIn}>
              <Card className="text-center h-full flex flex-col items-center justify-center">
                <p className="text-2xl sm:text-3xl font-bold mb-1" style={{ color: m.color }}>{m.value}</p>
                <p className="text-xs text-[#8899AA]">{m.label}</p>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>
    </Section>
  );
}

/* ── Competencia ── */

type YN = boolean;
const COMP: { name: string; legal: YN; banking: YN; remesas: YN; ia: YN; migrantes: YN }[] = [
  { name: 'MIGRO', legal: true, banking: true, remesas: true, ia: true, migrantes: true },
  { name: 'Wise', legal: false, banking: true, remesas: true, ia: false, migrantes: false },
  { name: 'Remitly', legal: false, banking: false, remesas: true, ia: false, migrantes: false },
  { name: 'Western Union', legal: false, banking: false, remesas: true, ia: false, migrantes: false },
  { name: 'Revolut', legal: false, banking: true, remesas: true, ia: false, migrantes: false },
  { name: 'N26', legal: false, banking: true, remesas: false, ia: false, migrantes: false },
];

function YesNo({ v }: { v: boolean }) {
  return v
    ? <Check className="w-5 h-5 text-[#00D4AA] mx-auto" />
    : <X className="w-5 h-5 text-[#FF6B6B]/50 mx-auto" />;
}

function Competition() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.05 });

  return (
    <Section id="competencia">
      <SectionTitle>Nadie más hace esto.</SectionTitle>

      {/* Desktop */}
      <motion.div ref={ref} initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={fadeUp}
        className="hidden sm:block overflow-x-auto rounded-xl border border-white/5"
      >
        <table className="w-full text-sm text-center">
          <thead>
            <tr className="border-b border-white/10 text-[#8899AA]">
              <th className="px-5 py-4 text-left font-semibold" />
              <th className="px-4 py-4 font-semibold">Legal</th>
              <th className="px-4 py-4 font-semibold">Banking</th>
              <th className="px-4 py-4 font-semibold">Remesas</th>
              <th className="px-4 py-4 font-semibold">IA</th>
              <th className="px-4 py-4 font-semibold">Migrantes</th>
            </tr>
          </thead>
          <tbody>
            {COMP.map((c, i) => (
              <tr key={i} className={`${i === 0 ? 'bg-[#00D4AA]/5 font-bold' : i % 2 ? 'bg-[#141E33]' : 'bg-[#0F1A2E]'}`}>
                <td className={`px-5 py-4 text-left ${i === 0 ? 'text-[#00D4AA]' : 'text-white'}`}>{c.name}</td>
                <td className="px-4 py-4"><YesNo v={c.legal} /></td>
                <td className="px-4 py-4"><YesNo v={c.banking} /></td>
                <td className="px-4 py-4"><YesNo v={c.remesas} /></td>
                <td className="px-4 py-4"><YesNo v={c.ia} /></td>
                <td className="px-4 py-4"><YesNo v={c.migrantes} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </motion.div>

      {/* Mobile cards */}
      <div className="sm:hidden grid gap-4">
        {COMP.map((c, i) => (
          <motion.div key={i} custom={i} initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={fadeUp}>
            <Card className={i === 0 ? 'border border-[#00D4AA]/30' : ''}>
              <h3 className={`text-base font-bold mb-3 ${i === 0 ? 'text-[#00D4AA]' : 'text-white'}`}>{c.name}</h3>
              <div className="grid grid-cols-5 gap-2 text-center text-xs text-[#8899AA]">
                {(['legal', 'banking', 'remesas', 'ia', 'migrantes'] as const).map(k => (
                  <div key={k} className="flex flex-col items-center gap-1">
                    <YesNo v={c[k]} />
                    <span className="capitalize">{k}</span>
                  </div>
                ))}
              </div>
            </Card>
          </motion.div>
        ))}
      </div>

      <motion.p
        initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={fadeUp} custom={7}
        className="mt-10 text-center font-heading italic text-[#FFD166] text-sm sm:text-base"
      >
        MOAT: El único player que combina legal + banking + remesas.
      </motion.p>
    </Section>
  );
}

/* ── Roadmap ── */

const PHASES = [
  { phase: 'Fase 1', range: 'M1-M6', title: 'Fundación', items: ['Smart contracts', 'Circle', 'Unnax', 'CASP'], color: '#00D4AA' },
  { phase: 'Fase 2', range: 'M4-M9', title: 'Remesas', items: ['Bitso', 'Bridge', 'Liquidity pools', 'PI'], color: '#FFD166' },
  { phase: 'Fase 3', range: 'M7-M12', title: 'Tarjetas', items: ['Enfuce/Nium', 'Pomelo', 'Authorizer'], color: '#FF6B6B' },
  { phase: 'Fase 4', range: 'M10-M18', title: 'Escala', items: ['Expansión LATAM', 'Premium', 'API', 'EMI'], color: '#FFFFFF' },
];

function Roadmap() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.05 });

  return (
    <Section id="roadmap">
      <SectionTitle>Roadmap 18 meses</SectionTitle>

      {/* Desktop timeline */}
      <div ref={ref} className="hidden lg:block relative">
        {/* Line */}
        <motion.div
          initial={{ scaleX: 0 }} animate={inView ? { scaleX: 1 } : {}}
          transition={{ duration: 0.8, ease: 'easeOut' }}
          className="absolute top-4 left-0 right-0 h-0.5 bg-white/10 origin-left"
        />
        <div className="grid grid-cols-4 gap-6">
          {PHASES.map((p, i) => (
            <motion.div key={i} custom={i} initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={fadeUp} className="pt-10 relative">
              {/* Dot */}
              <motion.div
                initial={{ scale: 0 }} animate={inView ? { scale: 1 } : {}}
                transition={{ delay: 0.3 + i * 0.15 }}
                className="absolute top-1.5 left-1/2 -translate-x-1/2 w-5 h-5 rounded-full border-2"
                style={{ borderColor: p.color, backgroundColor: `${p.color}20` }}
              />
              <Card>
                <span className="text-xs tracking-widest uppercase text-[#8899AA]">{p.phase} &middot; {p.range}</span>
                <h3 className="text-base font-bold mt-2 mb-3" style={{ color: p.color }}>{p.title}</h3>
                <ul className="space-y-1">
                  {p.items.map(it => (
                    <li key={it} className="text-xs text-[#8899AA] flex items-center gap-2">
                      <span className="w-1 h-1 rounded-full flex-none" style={{ backgroundColor: p.color }} />
                      {it}
                    </li>
                  ))}
                </ul>
              </Card>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Mobile: vertical cards */}
      <div className="lg:hidden grid gap-4">
        {PHASES.map((p, i) => (
          <motion.div key={i} custom={i} initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={fadeUp}>
            <Card borderColor={p.color}>
              <span className="text-xs tracking-widest uppercase text-[#8899AA]">{p.phase} &middot; {p.range}</span>
              <h3 className="text-base font-bold mt-2 mb-3" style={{ color: p.color }}>{p.title}</h3>
              <ul className="space-y-1">
                {p.items.map(it => (
                  <li key={it} className="text-xs text-[#8899AA] flex items-center gap-2">
                    <span className="w-1 h-1 rounded-full flex-none" style={{ backgroundColor: p.color }} />
                    {it}
                  </li>
                ))}
              </ul>
            </Card>
          </motion.div>
        ))}
      </div>
    </Section>
  );
}

/* ── Proyecciones ── */

const PROJECTIONS = [
  { year: 'Y1', value: 1.8, pct: 10 },
  { year: 'Y2', value: 6.5, pct: 36 },
  { year: 'Y3', value: 18, pct: 100 },
];

function Projections() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.05 });

  return (
    <Section id="proyecciones">
      <SectionTitle>Proyección financiera</SectionTitle>
      <div ref={ref} className="grid gap-8 lg:grid-cols-[2fr_1fr] items-end">
        <motion.div initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={fadeUp}>
          <div className="flex items-end justify-center gap-8 sm:gap-14 h-64 mb-6">
            {PROJECTIONS.map((p, i) => (
              <div key={i} className="flex flex-col items-center gap-3 flex-1 max-w-[140px]">
                <motion.div
                  initial={{ height: 0 }}
                  animate={inView ? { height: `${p.pct}%` } : {}}
                  transition={{ duration: 0.9, delay: 0.2 + i * 0.15, ease: [.22, 1, .36, 1] as [number, number, number, number] }}
                  className="w-full rounded-t-lg bg-gradient-to-t from-[#00D4AA]/50 to-[#00D4AA] relative"
                >
                  <span className="absolute -top-7 left-1/2 -translate-x-1/2 text-white font-bold text-sm whitespace-nowrap">
                    {`${p.value}M\u202F€`}
                  </span>
                </motion.div>
                <span className="text-sm text-[#8899AA] font-semibold">{p.year}</span>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={fadeUp} custom={2}>
          <Card>
            <div className="space-y-4">
              <div>
                <p className="text-xs text-[#8899AA]">Y3 EBITDA</p>
                <p className="text-2xl font-bold text-[#00D4AA]">~30%</p>
              </div>
              <div>
                <p className="text-xs text-[#8899AA]">Usuarios Y3</p>
                <p className="text-2xl font-bold text-white">50K+</p>
              </div>
            </div>
          </Card>
        </motion.div>
      </div>

      <motion.p
        initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={fadeUp} custom={3}
        className="mt-10 text-center font-heading italic text-[#E8ECF1]/80 text-sm sm:text-base"
      >
        10x crecimiento impulsado por activación banking sobre base legal.
      </motion.p>
    </Section>
  );
}

/* ── Regulación ── */

const REGS = [
  { title: 'CASP', cost: '60K\u202F€', time: '4-6 meses', entity: 'CNMV', color: '#00D4AA' },
  { title: 'PI', cost: '125K\u202F€', time: '6-12 meses', entity: 'BdE', color: '#FFD166' },
  { title: 'EMI', cost: '350K\u202F€', time: '12-18 meses', entity: 'BdE', color: '#FF6B6B' },
];

function Regulation() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.05 });

  return (
    <Section id="regulacion">
      <SectionTitle>Camino regulatorio</SectionTitle>
      <div ref={ref} className="grid gap-6 sm:grid-cols-3">
        {REGS.map((r, i) => (
          <motion.div key={i} custom={i} initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={fadeUp}>
            <Card borderColor={r.color} className="text-center">
              <Landmark className="w-8 h-8 mx-auto mb-3" style={{ color: r.color }} />
              <h3 className="text-xl font-bold text-white mb-4">{r.title}</h3>
              <div className="space-y-2 text-sm">
                <p><span className="text-[#8899AA]">Capital: </span><span className="text-[#E8ECF1] font-semibold">{r.cost}</span></p>
                <p><span className="text-[#8899AA]">Tiempo: </span><span className="text-[#E8ECF1] font-semibold">{r.time}</span></p>
                <p><span className="text-[#8899AA]">Regulador: </span><span className="font-semibold" style={{ color: r.color }}>{r.entity}</span></p>
              </div>
            </Card>
          </motion.div>
        ))}
      </div>
    </Section>
  );
}

/* ── The Ask / CTA ── */

function TheAsk() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.05 });

  return (
    <section id="cta" className="relative min-h-screen flex items-center justify-center px-5 overflow-hidden">
      {/* Glow effects */}
      <div className="pointer-events-none absolute bottom-[-20%] left-[-10%] w-[500px] h-[500px] rounded-full bg-[#00D4AA]/6 blur-[140px]" />
      <div className="pointer-events-none absolute top-[-10%] right-[-5%] w-[400px] h-[400px] rounded-full bg-[#FF6B6B]/4 blur-[120px]" />

      <div ref={ref} className="relative z-10 text-center max-w-3xl mx-auto">
        <motion.span
          initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={fadeUp}
          className="inline-block mb-4 text-[#00D4AA] text-sm tracking-[0.3em] uppercase font-semibold"
        >
          Seed Round
        </motion.span>

        <motion.p
          initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={fadeUp} custom={1}
          className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-10"
        >
          {'1.500.000\u202F€'}
        </motion.p>

        <motion.div
          initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={fadeUp} custom={2}
          className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-12"
        >
          {[
            { pct: '40%', label: 'Producto & Tech' },
            { pct: '25%', label: 'Licencias & Legal' },
            { pct: '20%', label: 'Go-to-Market' },
            { pct: '15%', label: 'Operaciones' },
          ].map(f => (
            <div key={f.label} className="text-center">
              <p className="text-xl font-bold text-[#00D4AA]">{f.pct}</p>
              <p className="text-xs text-[#8899AA] mt-1">{f.label}</p>
            </div>
          ))}
        </motion.div>

        <motion.p
          initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={fadeUp} custom={3}
          className="font-heading italic text-[#E8ECF1]/80 text-base sm:text-lg mb-12 leading-relaxed max-w-2xl mx-auto"
        >
          La migración es el producto más caro del mundo. Nosotros lo convertimos en el más accesible.
        </motion.p>

        <motion.div initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={scaleIn} custom={4}>
          <a
            href="mailto:agusvc@gmail.com"
            className="inline-flex items-center gap-3 px-10 py-4 rounded-xl bg-[#00D4AA] text-[#0A1628] font-bold text-base sm:text-lg
              shadow-lg shadow-[#00D4AA]/25 transition-all duration-300
              hover:shadow-[#00D4AA]/40 hover:shadow-xl hover:scale-[1.03] active:scale-[0.98]"
          >
            <Mail className="w-5 h-5" />
            Hablemos &rarr; agusvc@gmail.com
          </a>
        </motion.div>

        <motion.p
          initial="hidden" animate={inView ? 'visible' : 'hidden'} variants={fadeUp} custom={5}
          className="mt-6 text-sm text-[#8899AA]"
        >
          migro.es
        </motion.p>
      </div>
    </section>
  );
}

/* ──────────────────── MAIN PAGE ──────────────────── */

export function PitchLanding() {
  return (
    <>
      {/* OG Meta tags via Helmet-less approach: set in index.html or here for SPA */}
      <div
        className="min-h-screen antialiased"
        style={{
          fontFamily: "'Inter', 'Calibri', system-ui, sans-serif",
          background: 'linear-gradient(180deg, #0A1628 0%, #0D1F3C 50%, #0A1628 100%)',
          color: '#E8ECF1',
        }}
      >
        <style>{`
          .font-heading { font-family: Georgia, 'Inter Tight', serif; }
          @media (prefers-reduced-motion: reduce) {
            *, *::before, *::after { animation-duration: 0.01ms !important; transition-duration: 0.01ms !important; }
          }
        `}</style>

        <Hero />
        <TenTruths />
        <Problem />
        <Solution />
        <Market />
        <WhyNow />
        <Traction />
        <Product />
        <BusinessModel />
        <UnitEconomics />
        <Competition />
        <Roadmap />
        <Projections />
        <Regulation />
        <TheAsk />
      </div>
    </>
  );
}
